import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Button,
  Icon,
  Badge,
  Flex,
  Image,
} from "@chakra-ui/react";
import {
  FaSolarPanel,
  FaBuilding,
  FaChartLine,
  FaArrowRight,
  FaCheckCircle,
  FaBolt,
  FaLeaf,
  FaDollarSign,
  FaShieldAlt,
  FaStar,
  FaQuoteLeft,
  FaPhone,
  FaPencilAlt,
  FaCalculator,
  FaFacebook,
  FaInstagram,
  FaCalendarAlt,
} from "react-icons/fa";
import { keyframes } from "@emotion/react";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";
import { GoogleReviews } from "../../../components/chakra/GoogleReviews/GoogleReviews";

const SOLAR_GOLD = "#C69214";
const SOLAR_GOLD_HOVER = "#a07e10";
const NAVY = "#141C49";

// Floating button animations
const chargeFill = keyframes`
  0%   { height: 0% }
  60%  { height: 100% }
  78%  { height: 100% }
  100% { height: 0% }
`;

const boltFlash = keyframes`
  0%, 52%  { opacity: 1; }
  62%       { opacity: 0.08; }
  72%       { opacity: 1; }
  82%       { opacity: 0.08; }
  92%, 100% { opacity: 1; }
`;

const panelPulse = keyframes`
  0%, 100% { opacity: 0.22; }
  50%       { opacity: 0.6; }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 5px 2px 22px rgba(20,28,73,0.52), 0 0 12px rgba(198,146,20,0.2); }
  50%       { box-shadow: 5px 2px 32px rgba(20,28,73,0.7), 0 0 24px rgba(198,146,20,0.58); }
`;

const HERO_VIDEO_ID = "HlszGpa0Isc";
const HERO_VIDEO_ASPECT = 16 / 9;

const SolarHome: React.FC = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const [heroVideoSize, setHeroVideoSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateHeroVideoSize = () => {
      const el = heroRef.current;
      if (!el) return;
      const { offsetWidth: w, offsetHeight: h } = el;
      if (!w || !h) return;
      if (w / h > HERO_VIDEO_ASPECT) {
        setHeroVideoSize({ width: w, height: w / HERO_VIDEO_ASPECT });
      } else {
        setHeroVideoSize({ width: h * HERO_VIDEO_ASPECT, height: h });
      }
    };
    updateHeroVideoSize();
    window.addEventListener("resize", updateHeroVideoSize);
    return () => window.removeEventListener("resize", updateHeroVideoSize);
  }, []);

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar"
        fallbackTitle="One Solar Australia – Your Queensland Solar Specialists"
        fallbackDescription="Queensland solar specialists helping families switch, add and upgrade. CEC accredited installers, premium brands, 10+ years experience."
      />
      <NavbarWithCallToAction />

      {/* ── Floating Quote Button (solar + battery charging icon) ── */}
      <Box
        as={RouterLink}
        to="/solar/quote"
        position="fixed"
        left="14px"
        top="50%"
        transform="translateY(-50%)"
        zIndex={100}
        w="62px"
        h="74px"
        bg={NAVY}
        borderRadius="14px"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="5px"
        cursor="pointer"
        overflow="hidden"
        color="white"
        _hover={{ bg: SOLAR_GOLD, transform: "translateY(-50%) scale(1.08)" }}
        transition="background-color 0.22s, transform 0.22s"
        sx={{ animation: `${glowPulse} 2.6s ease-in-out infinite` }}
      >
        {/* Solar panel — background layer, pulses gently */}
        <Box
          position="absolute"
          inset={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          pointerEvents="none"
          sx={{ animation: `${panelPulse} 2.6s ease-in-out infinite` }}
        >
          <Icon as={FaSolarPanel} boxSize={14} color={SOLAR_GOLD} />
        </Box>

        {/* Get label */}
        <Text
          fontSize="8px"
          fontWeight="800"
          letterSpacing="0.12em"
          textTransform="uppercase"
          color="white"
          lineHeight={1}
          position="relative"
          zIndex={2}
        >
          Get
        </Text>

        {/* Battery charging icon — foreground */}
        <Box position="relative" w="30px" h="28px" zIndex={2} flexShrink={0}>
          {/* Battery outline */}
          <svg
            width="30"
            height="28"
            viewBox="0 0 30 28"
            fill="none"
            style={{ position: "absolute", top: 0, left: 0, zIndex: 1 }}
          >
            {/* Body */}
            <rect x="1" y="4" width="23" height="20" rx="3" stroke="white" strokeWidth="2.4" />
            {/* Positive terminal */}
            <rect x="24.2" y="9" width="4.8" height="10" rx="2" fill="white" />
          </svg>

          {/* Animated gold fill — clips bottom-to-top like charging */}
          <Box
            position="absolute"
            left="4px"
            top="7px"
            w="17px"
            h="14px"
            overflow="hidden"
            borderRadius="1.5px"
            zIndex={2}
          >
            <Box
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              bg={SOLAR_GOLD}
              borderRadius="1.5px"
              sx={{ animation: `${chargeFill} 2.6s ease-in-out infinite` }}
            />
          </Box>

          {/* Lightning bolt — flashes when battery fills */}
          <Box
            position="absolute"
            inset={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={3}
            sx={{ animation: `${boltFlash} 2.6s ease-in-out infinite` }}
          >
            <Icon as={FaBolt} color="white" w="10px" h="10px" />
          </Box>
        </Box>

        {/* Label */}
        <Text
          fontSize="8px"
          fontWeight="800"
          letterSpacing="0.12em"
          textTransform="uppercase"
          color="white"
          lineHeight={1}
          position="relative"
          zIndex={2}
        >
          Quote
        </Text>
      </Box>

      {/* ── Hero ── */}
      <Box
        ref={heroRef}
        position="relative"
        zIndex={0}
        overflow="hidden"
        color="white"
        py={{ base: 24, md: 40 }}
        backgroundImage="url('/assets/solar-assets/asset%2037.jpeg')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(14, 20, 55, 0.62)",
          zIndex: 0,
        }}
      >
        {/* Hero background video */}
        {heroVideoSize.width > 0 && (
          <Box
            as="iframe"
            position="absolute"
            top="50%"
            left="50%"
            width={`${heroVideoSize.width}px`}
            height={`${heroVideoSize.height}px`}
            transform="translate(-50%, -50%)"
            zIndex={-1}
            pointerEvents="none"
            border="0"
            src={`https://www.youtube.com/embed/${HERO_VIDEO_ID}?autoplay=1&mute=1&loop=1&playlist=${HERO_VIDEO_ID}&controls=0&showinfo=0&rel=0&modestbranding=1&iv_load_policy=3&disablekb=1&playsinline=1`}
            title="One Solar hero background video"
            allow="autoplay; encrypted-media; picture-in-picture"
            aria-hidden="true"
          />
        )}

        {/* Social icons — absolutely positioned, vertically centred, left of container */}
        <VStack
          position="absolute"
          left={{ base: 3, md: "calc(25% - 3.5rem)" }}
          top="50%"
          transform="translateY(-50%)"
          spacing={4}
          zIndex={2}
        >
          <Box
            as="a"
            href="https://www.facebook.com/One-Solar-100522258324560"
            target="_blank"
            rel="noopener noreferrer"
            w={9} h={9}
            borderRadius="full"
            border="1px solid"
            borderColor="whiteAlpha.600"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="white"
            _hover={{ borderColor: "white", bg: "whiteAlpha.200" }}
            transition="all 0.2s"
          >
            <FaFacebook size={15} />
          </Box>
          <Box
            as="a"
            href="https://www.instagram.com/onesolar.australia.official"
            target="_blank"
            rel="noopener noreferrer"
            w={9} h={9}
            borderRadius="full"
            border="1px solid"
            borderColor="whiteAlpha.600"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="white"
            _hover={{ borderColor: "white", bg: "whiteAlpha.200" }}
            transition="all 0.2s"
          >
            <FaInstagram size={15} />
          </Box>
        </VStack>

        <Container maxW="container.xl" position="relative" zIndex={1} pl={{ base: 6, md: "25%" }}>
          <VStack spacing={15} align="flex-start" textAlign="left">
            {/* Subtitle */}
            <Text
              fontSize={{ base: "xs", md: "sm" }}
              fontWeight="600"
              letterSpacing="widest"
              textTransform="uppercase"
              color="whiteAlpha.800"
              mb={-2}
            >
              Your Queensland Solar &amp; Battery Specialists
            </Text>

            {/* Heading */}
            <Heading
              as="h1"
              fontSize={{ base: "4xl", md: "6xl", lg: "7xl" }}
              fontWeight="black"
              lineHeight="1.05"
              letterSpacing="tight"
              textTransform="uppercase"
              textAlign="left"
            >
              One Solar,<br />The Smart Choice
            </Heading>

            {/* Buttons */}
            <HStack spacing={3} pt={2} flexWrap="wrap" justify="flex-start">
              <Button
                as={RouterLink}
                to="/solar/quote"
                size="sm"
                h="37px"
                bg={NAVY}
                color="white"
                border="1px solid"
                borderColor={SOLAR_GOLD}
                leftIcon={<Icon as={FaPencilAlt} color={SOLAR_GOLD} />}
                _hover={{ bg: SOLAR_GOLD, borderColor: SOLAR_GOLD, color: "white" }}
                px={5}
                transition="all 0.2s"
                textTransform="uppercase"
                letterSpacing="wider"
                fontSize="xs"
                fontWeight="700"
                sx={{ "&:hover svg": { color: NAVY } }}
              >
                Free Quote
              </Button>
              <Button
                as={RouterLink}
                to="/solar/calculator"
                size="sm"
                h="37px"
                bg={NAVY}
                color="white"
                border="1px solid"
                borderColor={SOLAR_GOLD}
                leftIcon={<Icon as={FaCalculator} color={SOLAR_GOLD} />}
                _hover={{ bg: SOLAR_GOLD, borderColor: SOLAR_GOLD, color: "white" }}
                px={5}
                transition="all 0.2s"
                textTransform="uppercase"
                letterSpacing="wider"
                fontSize="xs"
                fontWeight="700"
                sx={{ "&:hover svg": { color: NAVY } }}
              >
                Savings Calculator
              </Button>
              <Button
                as="a"
                href="https://onegroupaustralasiacomau.pipedrive.com/scheduler/QXbMMatL/solar-assessment-booking"
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                h="37px"
                bg={NAVY}
                color="white"
                border="1px solid"
                borderColor={SOLAR_GOLD}
                leftIcon={<Icon as={FaCalendarAlt} color={SOLAR_GOLD} />}
                _hover={{ bg: SOLAR_GOLD, borderColor: SOLAR_GOLD, color: "white" }}
                px={5}
                transition="all 0.2s"
                textTransform="uppercase"
                letterSpacing="wider"
                fontSize="xs"
                fontWeight="700"
                sx={{ "&:hover svg": { color: NAVY } }}
              >
                Book In-Home Assessment
              </Button>
            </HStack>
            <Text fontSize="sm" color="whiteAlpha.700" pt={1}>
              Or call us:{" "}
              <Text as="a" href="tel:1300663765" fontWeight="700" color="white" _hover={{ color: SOLAR_GOLD }}>
                1300 663 765
              </Text>
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* ── Hero subtitle strip ── */}
      <Box bg="white" py={5} borderBottom="1px solid" borderColor="gray.100">
        <Container maxW="container.xl">
          <Text
            textAlign="center"
            fontSize={{ base: "sm", md: "md" }}
            fontWeight="600"
            color={NAVY}
          >
            Queensland Solar Specialists Helping Families Switch, Add &amp; Upgrade
          </Text>
        </Container>
      </Box>

      {/* ── Stats — parallax ── */}
      <Box
        position="relative"
        color="white"
        py={20}
        backgroundImage="url('/assets/solar-assets/asset%2020.jpeg')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(20, 20, 20, 0.68)",
          zIndex: 0,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={8}>
            <Heading size="xl" textAlign="center" textShadow="0 2px 4px rgba(0,0,0,0.4)">
              3,007 Queensland Families Already Saving
            </Heading>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8} w="full" pt={4}>
              {[
                { value: "3,007", label: "Systems Installed" },
                { value: "~27.59 MW", label: "Solar Installed" },
                { value: "~8.749 MWh", label: "Battery Capacity Installed" },
                { value: "7+", label: "Years Experience" },
              ].map((s) => (
                <VStack key={s.label} spacing={2}>
                  <Heading size="2xl" color="white" textShadow="0 2px 8px rgba(0,0,0,0.4)">{s.value}</Heading>
                  <Text fontSize="md" color="rgba(255,255,255,0.9)" textShadow="0 1px 2px rgba(0,0,0,0.3)">{s.label}</Text>
                </VStack>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* ── Services ── */}
      <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
        <VStack spacing={4} mb={14} textAlign="center">
          <Heading size="xl" color="gray.800">Get Your Solar & Battery Solutions</Heading>
          <Text fontSize="lg" color="gray.600" maxW="2xl">
            Tailored solar & battery packages for every home, business, and investment property
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
          {[
            {
              img: "/assets/solar-assets/asset%2019.jpeg",
              alt: "Aerial view of residential solar installation Queensland",
              icon: FaSolarPanel,
              title: "Residential Solar",
              body: "Complete home solar systems sized for your household. Reduce your bills, increase your energy independence, and add lasting value to your property.",
              link: "/solar/services/residential",
            },
            {
              img: "/assets/solar-assets/asset%2033.jpeg",
              alt: "Commercial solar installation on business roof",
              icon: FaBuilding,
              title: "Commercial Solar",
              body: "Scalable business solutions that slash operating costs, demonstrate environmental leadership, and deliver strong ROI from day one.",
              link: "/solar/services/commercial",
            },
            {
              img: "/assets/solar-assets/asset%2021.jpeg",
              alt: "Solar technician performing installation",
              icon: FaChartLine,
              title: "Solar Monitoring",
              body: "Real-time performance tracking so you always know how much energy you're generating, consuming, and exporting back to the grid.",
              link: "/solar/services/monitoring",
            },
          ].map((card) => (
            <Box
              key={card.title}
              bg="white"
              borderRadius="xl"
              border="1px solid"
              borderColor="gray.200"
              _hover={{ transform: "translateY(-4px)", shadow: "2xl", borderColor: SOLAR_GOLD }}
              transition="all 0.3s"
              overflow="hidden"
            >
              <Box h="220px" overflow="hidden">
                <Box
                  as="img"
                  src={card.img}
                  alt={card.alt}
                  w="full"
                  h="full"
                  objectFit="cover"
                  transition="transform 0.4s"
                  _groupHover={{ transform: "scale(1.05)" }}
                />
              </Box>
              <Box p={8}>
                <Icon as={card.icon} boxSize={10} color={SOLAR_GOLD} mb={4} />
                <Heading size="md" color={NAVY} mb={3}>{card.title}</Heading>
                <Text color="gray.600" fontSize="sm" mb={5} lineHeight="1.7">{card.body}</Text>
                <Button as={RouterLink} to={card.link} color={NAVY} variant="link" rightIcon={<FaArrowRight />} _hover={{ color: SOLAR_GOLD }}>
                  Learn More
                </Button>
              </Box>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* ── Your Path to Solar Savings ── */}
      <Box bg="gray.50" py={{ base: 16, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={14} textAlign="center">
            <Text fontSize="xs" fontWeight="700" letterSpacing="widest" textTransform="uppercase" color={SOLAR_GOLD}>
              Your Path to Solar Savings
            </Text>
            <Heading size="xl" color={NAVY}>From First Call to Live System in 2–4 Weeks</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              We handle everything — so you can start saving sooner.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10}>
            {[
              {
                step: "01",
                img: "/assets/solar-assets/asset%2013.jpeg",
                title: "We Assess Your Property",
                body: "One of our Energy Advisors evaluates your property, energy usage, and goals — no obligation, no pressure. Just expert advice tailored to your situation.",
              },
              {
                step: "02",
                img: "/assets/solar-assets/asset%2018.jpeg",
                title: "We Design Your System",
                body: "We design a system optimised for your roof, your usage patterns, and your budget — including battery storage options and monitoring packages.",
              },
              {
                step: "03",
                img: "/assets/solar-assets/asset%2033.jpeg",
                title: "You Start Saving",
                body: "Our CEC-accredited installers complete the job to the highest standard. Grid connection and commissioning included — you're live and saving in as little as 4–6 weeks.",
              },
            ].map((item) => (
              <Box key={item.step} bg="white" borderRadius="2xl" overflow="hidden" boxShadow="md" border="1px solid" borderColor="gray.100" _hover={{ boxShadow: "xl", borderColor: SOLAR_GOLD }} transition="all 0.3s">
                <Box h="200px" overflow="hidden" position="relative">
                  <Box as="img" src={item.img} alt={item.title} w="full" h="full" objectFit="cover" />
                  <Box
                    position="absolute"
                    top={4}
                    left={4}
                    w={12}
                    h={12}
                    bg={SOLAR_GOLD}
                    borderRadius="full"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontWeight="black"
                    fontSize="lg"
                    color="white"
                    boxShadow="0 2px 8px rgba(198,146,20,0.5)"
                  >
                    {item.step}
                  </Box>
                </Box>
                <Box p={6}>
                  <Heading size="md" color={NAVY} mb={3}>{item.title}</Heading>
                  <Text color="gray.600" fontSize="sm" lineHeight="1.7">{item.body}</Text>
                </Box>
              </Box>
            ))}
          </SimpleGrid>

          <VStack mt={12}>
            <Button
              as={RouterLink}
              to="/solar/quote"
              bg={SOLAR_GOLD}
              color="white"
              size="lg"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: SOLAR_GOLD_HOVER }}
              transition="all 0.2s"
            >
              Book My Free Assessment
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* ── Switch to Solar — split ── */}
      <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
          <VStack align="flex-start" spacing={6}>
            <Text fontSize="xs" fontWeight="700" letterSpacing="widest" textTransform="uppercase" color={SOLAR_GOLD}>
              Switch to Solar
            </Text>
            <Heading size="xl" color={NAVY} lineHeight="1.2">
              Keep More Money in Your Pocket
            </Heading>
            <Text color="gray.600" fontSize="lg" lineHeight="1.8">
              Rising energy costs are hitting Australian families hard. Solar is the smartest long-term investment you can make — reduce your bills from day one and protect yourself from future price rises.
            </Text>
            <VStack align="start" spacing={3}>
              {[
                "Reduce electricity bills by up to 70%",
                "Feed excess energy back to the grid",
                "Add value to your property",
                "25-year panel performance warranty",
                "Flexible finance options available",
              ].map((pt) => (
                <HStack key={pt}>
                  <Icon as={FaCheckCircle} color={SOLAR_GOLD} boxSize={4} flexShrink={0} />
                  <Text fontSize="sm" color="gray.700">{pt}</Text>
                </HStack>
              ))}
            </VStack>
            <HStack spacing={4} pt={2} flexWrap="wrap">
              <Button
                as={RouterLink}
                to="/solar/quote"
                bg={SOLAR_GOLD}
                color="white"
                size="lg"
                rightIcon={<FaArrowRight />}
                _hover={{ bg: SOLAR_GOLD_HOVER }}
                transition="all 0.2s"
              >
                Get Free Quote
              </Button>
              <Button
                as={RouterLink}
                to="/solar/calculator"
                variant="outline"
                borderColor={NAVY}
                color={NAVY}
                size="lg"
                _hover={{ bg: NAVY, color: "white" }}
                transition="all 0.2s"
              >
                See My Savings
              </Button>
            </HStack>
          </VStack>
          <Box borderRadius="2xl" overflow="hidden" h={{ base: "320px", md: "480px" }} position="relative" boxShadow="2xl">
            <Box as="img" src="/assets/solar-assets/asset%2038.jpeg" alt="Family enjoying the benefits of solar energy" w="full" h="full" objectFit="cover" />
            <Box position="absolute" bottom={0} left={0} right={0} p={5} bgGradient={`linear(to-t, ${NAVY}DD, transparent)`}>
              <Text fontSize="sm" fontWeight="700" color="white" letterSpacing="wider" textTransform="uppercase">
                Clean energy. Lower bills. Better future.
              </Text>
            </Box>
          </Box>
        </SimpleGrid>
      </Container>

      {/* ── Why Choose Solar — benefits ── */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={12} textAlign="center">
            <Heading size="xl" color={NAVY}>What Solar Does For You</Heading>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            {[
              { icon: FaBolt, title: "Energy Independence", body: "Generate your own clean energy and reduce reliance on the grid — forever." },
              { icon: FaDollarSign, title: "Lower Bills", body: "Save thousands on electricity over your system's lifetime, from day one." },
              { icon: FaLeaf, title: "Eco-Friendly", body: "Reduce your carbon footprint and contribute to a cleaner planet for your family." },
              { icon: FaShieldAlt, title: "25-Year Warranty", body: "Premium panels from world-leading brands, backed by industry-leading warranties." },
            ].map((b) => (
              <VStack key={b.title} p={7} bg="white" borderRadius="xl" boxShadow="md" spacing={4} align="flex-start" border="1px solid" borderColor="gray.100" _hover={{ borderColor: SOLAR_GOLD, boxShadow: "lg" }} transition="all 0.3s">
                <Icon as={b.icon} boxSize={9} color={SOLAR_GOLD} />
                <Heading size="sm" color={NAVY}>{b.title}</Heading>
                <Text color="gray.600" fontSize="sm" lineHeight="1.7">{b.body}</Text>
              </VStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Charli Cannon Sponsorship ── */}
      <Box
        position="relative"
        overflow="hidden"
        bg={NAVY}
        py={{ base: 16, md: 20 }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={14} alignItems="center">
            <Box borderRadius="2xl" overflow="hidden" boxShadow="2xl" h={{ base: "260px", md: "380px" }}>
              <Box as="img" src="/assets/solar-assets/asset%2032.jpeg" alt="Charli Cannon - One Solar Australia sponsored athlete" w="full" h="full" objectFit="cover" objectPosition="center top" />
            </Box>
            <VStack align="flex-start" spacing={5} color="white">
              <Text fontSize="xs" fontWeight="700" letterSpacing="widest" textTransform="uppercase" color={SOLAR_GOLD}>
                Official Sponsor
              </Text>
              <Heading size="xl" lineHeight="1.2">
                Proud Sponsors of Charli Cannon
              </Heading>
              <Text color="gray.300" fontSize="lg" lineHeight="1.8">
                We're proud to back Australian motocross rising star Charli Cannon — because at One Solar Australia, performance matters. The same discipline and preparation that drives Charli is what we bring to every installation.
              </Text>
              <Box bg="whiteAlpha.100" borderRadius="xl" px={6} py={4} borderLeft="4px solid" borderColor={SOLAR_GOLD}>
                <Text color={SOLAR_GOLD} fontWeight="700" fontSize="lg">Exclusive 10% Discount</Text>
                <Text color="gray.300" fontSize="sm" mt={1}>Mention Charli Cannon when you book your free quote and receive 10% off your system.</Text>
              </Box>
              <Button
                as={RouterLink}
                to="/solar/quote"
                size="lg"
                bg={SOLAR_GOLD}
                color="white"
                _hover={{ bg: SOLAR_GOLD_HOVER }}
                rightIcon={<FaArrowRight />}
                transition="all 0.2s"
              >
                Claim the Discount
              </Button>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Testimonials ── */}
      <Box bg="gray.50" py={{ base: 16, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={14} textAlign="center">
            <HStack spacing={1} justify="center">
              {[...Array(5)].map((_, i) => <Icon key={i} as={FaStar} color={SOLAR_GOLD} boxSize={5} />)}
            </HStack>
            <Heading size="xl" color={NAVY}>What Queensland Families Are Saying</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              Real customers. Real results. Real savings.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {[
              {
                img: "/assets/solar-assets/asset%2034.png",
                name: "Ryan",
                label: "QLD Homeowner",
                quote: "The team at One Solar were fantastic from start to finish. Our bills have dropped dramatically and the install was done in a single day. Couldn't be happier with the whole experience.",
              },
              {
                img: "/assets/solar-assets/asset%2035.png",
                name: "Mel",
                label: "3-BDR Homeowner",
                quote: "I was nervous about the whole process but the Energy Advisor made it so easy to understand. The system is performing even better than they estimated. Highly recommend One Solar.",
              },
              {
                img: "/assets/solar-assets/asset%2036.png",
                name: "Deik",
                label: "Lack Group",
                quote: "We installed solar across three of our commercial properties with One Solar. The ROI has been exceptional and the monitoring system gives us real visibility across all sites. Great team.",
              },
            ].map((t) => (
              <Box key={t.name} bg="white" borderRadius="2xl" boxShadow="md" p={8} border="1px solid" borderColor="gray.100" _hover={{ boxShadow: "xl", borderColor: SOLAR_GOLD }} transition="all 0.3s">
                <Icon as={FaQuoteLeft} color={SOLAR_GOLD} boxSize={6} mb={4} opacity={0.6} />
                <Text color="gray.700" fontSize="sm" lineHeight="1.8" mb={6} fontStyle="italic">
                  "{t.quote}"
                </Text>
                <HStack spacing={4}>
                  <Box w={14} h={14} borderRadius="full" overflow="hidden" border="2px solid" borderColor={SOLAR_GOLD} flexShrink={0}>
                    <Box as="img" src={t.img} alt={t.name} w="full" h="full" objectFit="cover" />
                  </Box>
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="700" color={NAVY} fontSize="sm">{t.name}</Text>
                    <Text fontSize="xs" color="gray.500">{t.label}</Text>
                    <HStack spacing="1px" mt={1}>
                      {[...Array(5)].map((_, i) => <Icon key={i} as={FaStar} color={SOLAR_GOLD} boxSize={3} />)}
                    </HStack>
                  </VStack>
                </HStack>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Google Reviews ── */}
      <GoogleReviews placeId={process.env.REACT_APP_GOOGLE_PLACE_ID || ""} />

      {/* ── Brand Partners ── */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={4} mb={12} textAlign="center">
          <Heading size="lg" color={NAVY}>Premium Equipment. Proven Performance.</Heading>
          <Text color="gray.600" maxW="xl">
            We only install equipment from the world's leading solar manufacturers — so your system performs for decades.
          </Text>
        </VStack>
        <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={6} alignItems="center">
          {[
            { img: "/assets/solar-assets/asset%2022.png", alt: "Sungrow" },
            { img: "/assets/solar-assets/asset%2023.png", alt: "Solar Analytics" },
            { img: "/assets/solar-assets/asset%2024.png", alt: "Risen Solar Technology" },
            { img: "/assets/solar-assets/asset%2025.png", alt: "Q Cells" },
            { img: "/assets/solar-assets/asset%2026.png", alt: "LG Chem" },
            { img: "/assets/solar-assets/asset%2027.png", alt: "Fronius" },
          ].map((b) => (
            <Box
              key={b.alt}
              p={4}
              bg="white"
              borderRadius="xl"
              border="1px solid"
              borderColor="gray.200"
              _hover={{ borderColor: SOLAR_GOLD, boxShadow: "md" }}
              transition="all 0.3s"
              display="flex"
              alignItems="center"
              justifyContent="center"
              h="80px"
            >
              <Image src={b.img} alt={b.alt} maxH="44px" maxW="130px" objectFit="contain" />
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* ── Why One Solar Australia — split ── */}
      <Box bg="gray.50">
        <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
            <VStack align="flex-start" spacing={6}>
              <Heading size="xl" color={NAVY}>We engineer value. Not just output.</Heading>
              <Text color="gray.600" fontSize="lg" lineHeight="1.8">
                Most solar companies sell you a system. One Solar Australia designs your energy infrastructure — sized for your property, integrated with your finances, and built to last 25+ years. Part of the One Group Prosperity Engine.
              </Text>
              <SimpleGrid columns={2} spacing={4} w="full">
                {[
                  { label: "Australian-Owned", desc: "Local expertise, national network — built for Australian conditions." },
                  { label: "CEC Accredited", desc: "All installers are Clean Energy Council certified professionals." },
                  { label: "25-Year Panels", desc: "Premium panels with industry-leading performance warranties." },
                  { label: "One Group Ecosystem", desc: "Solar connects to your finance and property investment strategy." },
                ].map((item) => (
                  <Box key={item.label} p={5} bg="white" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.100">
                    <Text fontWeight="700" color={NAVY} mb={2} fontSize="sm">{item.label}</Text>
                    <Text fontSize="sm" color="gray.600" lineHeight="1.6">{item.desc}</Text>
                  </Box>
                ))}
              </SimpleGrid>
              <Button
                as={RouterLink}
                to="/solar/contact"
                bg={SOLAR_GOLD}
                color="white"
                size="lg"
                rightIcon={<FaArrowRight />}
                _hover={{ bg: SOLAR_GOLD_HOVER }}
                transition="all 0.2s"
              >
                Book My Free Assessment
              </Button>
            </VStack>
            <Flex direction="column" gap={6}>
              <Box borderRadius="2xl" overflow="hidden" h="260px" position="relative" boxShadow="xl">
                <Box as="img" src="/assets/solar-assets/asset%2019.jpeg" alt="Aerial view of One Solar Australia residential installation in Queensland" w="full" h="full" objectFit="cover" />
                <Box position="absolute" bottom={0} left={0} right={0} p={4} bgGradient={`linear(to-t, ${NAVY}CC, transparent)`}>
                  <Text fontSize="xs" fontWeight="700" color="white" letterSpacing="wider" textTransform="uppercase">
                    Australian-Owned &amp; Operated — Lic. 88686
                  </Text>
                </Box>
              </Box>
              <SimpleGrid columns={2} spacing={4}>
                {[
                  { value: "3,007", label: "Systems Installed" },
                  { value: "15,000t", label: "CO₂ Offset" },
                  { value: "~27.59 MW", label: "Solar Installed" },
                  { value: "7+", label: "Years Experience" },
                ].map((s) => (
                  <Box key={s.label} p={5} bg="white" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.100" textAlign="center">
                    <Text fontWeight="900" color={SOLAR_GOLD} fontSize="2xl">{s.value}</Text>
                    <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" mt={1}>{s.label}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Flex>
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Flexible Finance ── */}
      <Box bg={NAVY} py={16}>
        <Container maxW="container.xl">
          <Flex direction={{ base: "column", md: "row" }} align="center" justify="space-between" gap={8}>
            <VStack align={{ base: "center", md: "start" }} spacing={3} textAlign={{ base: "center", md: "left" }}>
              <Text fontSize="xs" fontWeight="700" letterSpacing="widest" textTransform="uppercase" color={SOLAR_GOLD}>
                Flexible Finance
              </Text>
              <Heading size="lg" color="white">Go Solar with $0 Upfront</Heading>
              <Text color="gray.300" maxW="lg" fontSize="md" lineHeight="1.7">
                Don't let upfront cost hold you back. We offer flexible finance options so you can start saving on your energy bills immediately, with repayments that can be less than your current power bill.
              </Text>
            </VStack>
            <HStack spacing={4} flexShrink={0} flexWrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to="/solar/quote"
                size="lg"
                bg={SOLAR_GOLD}
                color="white"
                _hover={{ bg: SOLAR_GOLD_HOVER }}
                rightIcon={<FaArrowRight />}
                px={8}
                transition="all 0.2s"
              >
                Start Saving with $0 Upfront
              </Button>
              <Button
                as={RouterLink}
                to="/solar/contact"
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: SOLAR_GOLD, borderColor: SOLAR_GOLD }}
                px={8}
                transition="all 0.2s"
              >
                <HStack spacing={2}>
                  <FaPhone />
                  <Text>Contact Us Today</Text>
                </HStack>
              </Button>
            </HStack>
          </Flex>
        </Container>
      </Box>

      <SolarFooter />
    </Box>
  );
};

export default SolarHome;
